import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import VM from '@scratch/scratch-vm';

import {closeOnlineSearch} from '../reducers/modals';
import {activateTab, BLOCKS_TAB_INDEX} from '../reducers/editor-tab';
import {spriteUpload, costumeUpload} from '../lib/file-uploader.js';

import OnlineSearchModalComponent from '../components/online-search-modal/online-search-modal.jsx';

class OnlineSearchModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleSearchTermChange',
            'handleKeyDown',
            'handleSearch',
            'handleLoadMore',
            'handleSelectImage'
        ]);
        this.state = {
            searchTerm: '',
            results: [],
            offset: 0,
            hasMore: false,
            isLoading: false
        };
    }

    handleSearchTermChange (e) {
        this.setState({searchTerm: e.target.value});
    }

    handleKeyDown (e) {
        if (e.key === 'Enter') {
            this.handleSearch();
        }
    }

    handleSearch (isLoadMore = false) {
        const rawQuery = this.state.searchTerm.toLowerCase().trim();
        if (!rawQuery) return;

        if (!isLoadMore) {
            this.setState({ results: [], offset: 0, hasMore: false });
        }
        
        this.setState({ isLoading: true });
        
        const currentOffset = isLoadMore ? this.state.offset : 0;

        // Translate from vi to en using MyMemory API
        fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(rawQuery)}&langpair=vi|en`)
            .then(res => res.json())
            .then(translationData => {
                let query = rawQuery;
                if (translationData && translationData.responseData && translationData.responseData.translatedText) {
                    query = translationData.responseData.translatedText;
                }

                const limit = isLoadMore ? 12 : 11;
                // Use Wikimedia Commons API for free, keyless image search, dynamically setting gsrlimit
                const url = `https://en.wikipedia.org/w/api.php?action=query&generator=search&gsrsearch=${encodeURIComponent(query + ' filetype:bitmap')}&gsrnamespace=6&gsrlimit=${limit}&gsroffset=${currentOffset}&prop=imageinfo&iiprop=url&format=json&origin=*`;
                
                return fetch(url);
            })
            .then(res => res.json())
            .then(data => {
                const pages = (data.query && data.query.pages) ? Object.values(data.query.pages) : [];
                const newResults = pages.map(p => {
                    const info = p.imageinfo && p.imageinfo[0];
                    if (!info) return null;
                    return {
                        title: p.title.replace('File:', '').split('.')[0],
                        url: info.url,
                        thumbnail: info.url
                    };
                }).filter(r => r !== null);
                
                const hasMore = !!(data.continue && data.continue.gsroffset);
                const nextOffset = hasMore ? data.continue.gsroffset : 0;
                
                this.setState(prevState => ({
                    results: isLoadMore ? [...prevState.results, ...newResults] : newResults,
                    hasMore: hasMore,
                    offset: nextOffset,
                    isLoading: false
                }));
            })
            .catch(error => {
                console.error('Lỗi tìm kiếm Wikimedia:', error);
                this.setState({ isLoading: false });
            });
    }

    handleLoadMore () {
        if (!this.state.isLoading && this.state.hasMore) {
            this.handleSearch(true);
        }
    }

    handleSelectImage (url, title) {
        // Fetch the image as a blob
        fetch(url)
            .then(response => response.blob())
            .then(blob => {
                if (this.props.searchTarget === 'costume') {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const buffer = reader.result;
                        const fileType = blob.type || 'image/png';
                        const fileName = title || 'OnlineImage';
                        
                        costumeUpload(
                            buffer,
                            fileType,
                            this.props.vm.runtime.storage,
                            (vmCostumes) => {
                                Promise.all(vmCostumes.map(c => {
                                    c.name = fileName;
                                    return this.props.vm.addCostume(c.md5, c, this.props.vm.editingTarget.id);
                                })).then(() => {
                                    this.props.onRequestClose();
                                });
                            },
                            (error) => {
                                console.error('Error uploading costume:', error);
                            }
                        );
                    };
                    reader.readAsArrayBuffer(blob);
                } else if (this.props.searchTarget === 'backdrop') {
                    const img = new Image();
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = 480;
                        canvas.height = 360;
                        const ctx = canvas.getContext('2d');
                        
                        // Calculate scale to cover (no distortion)
                        const scale = Math.max(480 / img.width, 360 / img.height);
                        const x = (480 / 2) - (img.width / 2) * scale;
                        const y = (360 / 2) - (img.height / 2) * scale;
                        
                        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
                        
                        canvas.toBlob(scaledBlob => {
                            const reader = new FileReader();
                            reader.onload = () => {
                                const buffer = reader.result;
                                const fileType = 'image/png';
                                const fileName = title || 'OnlineImage';
                                
                                costumeUpload(
                                    buffer,
                                    fileType,
                                    this.props.vm.runtime.storage,
                                    (vmCostumes) => {
                                        Promise.all(vmCostumes.map(c => {
                                            c.name = fileName;
                                            return this.props.vm.addBackdrop(c.md5, c);
                                        })).then(() => {
                                            this.props.onRequestClose();
                                        });
                                    },
                                    (error) => {
                                        console.error('Error uploading backdrop:', error);
                                    }
                                );
                                URL.revokeObjectURL(img.src);
                            };
                            reader.readAsArrayBuffer(scaledBlob);
                        }, 'image/png');
                    };
                    img.onerror = () => {
                        URL.revokeObjectURL(img.src);
                        console.error('Failed to load blob into image for scaling');
                    };
                    img.src = URL.createObjectURL(blob);
                } else {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const buffer = reader.result;
                        const fileType = blob.type || 'image/png';
                        const fileName = title || 'OnlineImage';
                        
                        spriteUpload(
                            buffer,
                            fileType,
                            fileName,
                            this.props.vm.runtime.storage,
                            (newSprite) => {
                                this.props.vm.addSprite(newSprite).then(() => {
                                    this.props.onActivateBlocksTab();
                                    this.props.onRequestClose();
                                });
                            },
                            (error) => {
                                console.error('Error uploading sprite:', error);
                            }
                        );
                    };
                    reader.readAsArrayBuffer(blob);
                }
            })
            .catch(error => {
                console.error('Failed to fetch image:', error);
            });
    }

    render () {
        return (
            <OnlineSearchModalComponent
                onRequestClose={this.props.onRequestClose}
                searchTerm={this.state.searchTerm}
                results={this.state.results}
                hasMore={this.state.hasMore}
                isLoading={this.state.isLoading}
                onChangeSearchTerm={this.handleSearchTermChange}
                onKeyDown={this.handleKeyDown}
                onSearch={() => this.handleSearch(false)}
                onLoadMore={this.handleLoadMore}
                onSelectImage={this.handleSelectImage}
            />
        );
    }
}

OnlineSearchModal.propTypes = {
    onActivateBlocksTab: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    searchTarget: PropTypes.string,
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    vm: state.scratchGui.vm
});

const mapDispatchToProps = (dispatch, ownProps) => ({
    onRequestClose: ownProps.onRequestClose || (() => dispatch(closeOnlineSearch())),
    onActivateBlocksTab: () => dispatch(activateTab(BLOCKS_TAB_INDEX))
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(OnlineSearchModal);
